
import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';

interface PageTransitionProps {
  children: React.ReactNode;
}

const PageTransition = ({ children }: PageTransitionProps) => {
  const location = useLocation();
  const [displayChildren, setDisplayChildren] = useState(children);
  const [transitionState, setTransitionState] = useState<'enter' | 'exit' | 'idle'>('idle');
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    if (location.pathname !== prevPathRef.current) {
      prevPathRef.current = location.pathname;
      setTransitionState('exit');

      const exitTimer = setTimeout(() => {
        setDisplayChildren(children);
        window.scrollTo({ top: 0, behavior: 'instant' as const });
        setTransitionState('enter');

        const enterTimer = setTimeout(() => {
          setTransitionState('idle');
        }, 500);

        return () => clearTimeout(enterTimer);
      }, 300);

      return () => clearTimeout(exitTimer);
    } else {
      setDisplayChildren(children);
    }
  }, [children, location.pathname]);

  const getTransitionClass = () => {
    switch (transitionState) {
      case 'exit':
        return 'opacity-0 translate-y-4';
      case 'enter':
        return 'opacity-0 translate-y-4 animate-page-enter';
      case 'idle':
      default:
        return 'opacity-100 translate-y-0';
    }
  };

  return (
    <div
      className={`transition-all duration-300 ease-out ${getTransitionClass()}`}
    >
      {displayChildren}
    </div>
  );
};

export default PageTransition;
